import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import UserRolesMultiSelect from './UserRolesMultiSelect';
import { getUserActiveRoleTypes } from '../userFilterUtils';

const EditUserModal = ({ isOpen, onClose, onUpdateUser, user }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    roles: [],
    status: 'active',
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        name: user?.name || '',
        email: user?.email || '',
        roles: getUserActiveRoleTypes(user),
        status: user?.status || 'active',
      });
      setErrors({});
    }
  }, [user]);

  const statusOptions = [
    { value: 'active', label: 'Actif' },
    { value: 'inactive', label: 'Inactif' },
  ];

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Le nom est obligatoire';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = "L'email est obligatoire";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Format email invalide';
    }

    if (!formData?.roles?.length) {
      newErrors.roles = 'Sélectionnez au moins un rôle';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      await onUpdateUser(user?.id, formData);
      onClose();
    } catch {
      /* toast géré par le parent */
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  if (!isOpen || !user) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 animate-fade-in">
      <div className="bg-card border border-border rounded-lg shadow-modal w-full max-w-md animate-slide-in max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border sticky top-0 bg-card z-10">
          <h2 className="text-xl font-semibold text-foreground">Modifier l'utilisateur</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-muted transition-colors duration-200"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <Input
            label="Nom"
            type="text"
            placeholder="Nom complet"
            value={formData?.name}
            onChange={(e) => handleChange('name', e?.target?.value)}
            error={errors?.name}
            required
          />

          <Input
            label="Email"
            type="email"
            placeholder="user@example.com"
            value={formData?.email}
            onChange={(e) => handleChange('email', e?.target?.value)}
            error={errors?.email}
            required
          />

          <UserRolesMultiSelect
            value={formData?.roles}
            onChange={(roles) => handleChange('roles', roles)}
            error={errors?.roles}
            required
          />

          <Select
            label="Statut du compte"
            options={statusOptions}
            value={formData?.status}
            onChange={(value) => handleChange('status', value)}
            required
          />

          <div className="flex items-center gap-3 pt-4">
            <Button
              type="submit"
              variant="default"
              fullWidth
              loading={isSubmitting}
              iconName="Save"
              iconPosition="left"
              iconSize={18}
            >
              Enregistrer
            </Button>
            <Button type="button" variant="outline" fullWidth onClick={onClose} disabled={isSubmitting}>
              Annuler
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditUserModal;
